import * as React from 'react';
import { ChevronDown, ChevronUp, Pencil, X } from 'lucide-react';
import { Badge, type BadgeProps } from './Badge';
import { Button } from './Button';
import { Surface, type SurfaceProps } from './Card';
import { CallTimeline, type CallTimelineStep } from './CallTimeline';
import { Inline, Stack } from './Layout';
import { KeyValueRow } from './List';
import type { MapStatus } from './MapCallCard';
import { Loading } from './Spinner';
import { StatusPillMenu, type StatusPillOption } from './StatusPillMenu';
import { Text, TextLink } from './Text';

export interface MapCallHeadline {
  /** What the figure is: "ETA to pickup", "Time on scene", "Arrived in". */
  label: string;
  /** The figure: "6 min". */
  value: string;
  /** The line under it: "Arrives 2:41 PM · 2.8 mi". */
  detail?: string;
  flag?: { label: string; tone: BadgeProps['tone'] };
}

export interface MapSuggestedDriver {
  id: string;
  name: string;
  /** "Available · 4 min away · 1.9 mi". */
  detail: string;
}

export interface MapCallDetailViewProps
  extends Omit<SurfaceProps, 'children' | 'title'> {
  /** "Call #1042". */
  title: string;
  status: MapStatus;
  onClose: () => void;
  /** Left out for a user who may not edit calls; the pencil goes with it. */
  onEdit?: () => void;
  /**
   * With `onStatusChange`, the status pill beside the title opens a menu
   * of these. Left out for a user who may not edit calls.
   */
  statusOptions?: StatusPillOption[];
  onStatusChange?: (status: string) => void;
  /** The status is saving. */
  statusBusy?: boolean;
  headline?: MapCallHeadline;
  /**
   * Label and value pairs: account, vehicle, pickup, drop. A fact with
   * `onSelect` shows its value as a link, for a place the map can go to.
   */
  facts: { label: string; value: React.ReactNode; onSelect?: () => void }[];
  timeline: CallTimelineStep[];
  /** On the history map: choosing a step moves the replay to it. */
  onSelectTimelineStep?: (id: string) => void;
  /**
   * For a call with nobody on it: the closest drivers by drive time, nearest
   * first. Left out for a user who may not assign, and the section goes.
   */
  suggestions?: MapSuggestedDriver[];
  suggestionsLoading?: boolean;
  onAssignSuggestion?: (driverId: string) => void;
  /** The driver being assigned, whose button shows a spinner. */
  assigningDriverId?: string | null;
  /** Who is on the call and the menu to change it — a DriverAssignMenu. */
  driverMenu?: React.ReactNode;
  /** Buttons at the foot. */
  actions?: React.ReactNode;
}

/**
 * The panel that opens over the map when a call is chosen: its ETA in large
 * figures, the facts, a timeline, and for a call nobody is on, the closest
 * drivers with a button to send one.
 *
 *   <MapCallDetailView title="Call #1042" status={status} onClose={close}
 *     headline={{ label: 'ETA to pickup', value: '6 min' }}
 *     facts={facts} timeline={steps}
 *     className="absolute right-4 top-4 w-80" />
 *
 * It scrolls inside itself, so on a short window the map's own controls stay
 * where they are. The chevron folds it down to its title row and driver, so
 * a route drawn behind it can be seen. The fold belongs to one call: key the
 * view by call id, so the next call chosen opens in full. Where it floats is
 * the map's business: it takes a className for that and nothing else.
 */
export const MapCallDetailView = React.forwardRef<
  HTMLDivElement,
  MapCallDetailViewProps
>(function MapCallDetailView(
  {
    title,
    status,
    onClose,
    onEdit,
    statusOptions,
    onStatusChange,
    statusBusy = false,
    headline,
    facts,
    timeline,
    onSelectTimelineStep,
    suggestions,
    suggestionsLoading = false,
    onAssignSuggestion,
    assigningDriverId = null,
    driverMenu,
    actions,
    ...props
  },
  ref
) {
  const [folded, setFolded] = React.useState(false);
  return (
    <Surface
      ref={ref}
      bordered
      radius="lg"
      padding="md"
      elevation="overlay"
      {...props}
    >
      <Stack gap={4}>
        <Inline gap={2} align="center" justify="between">
          <Inline gap={2} align="center" wrap>
            <Text variant="h4">{title}</Text>
            <StatusPillMenu
              value={status.value ?? ''}
              label={status.label}
              phase={status.phase}
              tone={status.tone}
              options={statusOptions ?? []}
              onChange={statusOptions ? onStatusChange : undefined}
              busy={statusBusy}
            />
          </Inline>
          <Inline gap={1} align="center">
            <Button
              size="icon-sm"
              variant="ghost"
              onClick={() => setFolded((was) => !was)}
              aria-label={folded ? 'Show call details' : 'Hide call details'}
              aria-expanded={!folded}
            >
              {folded ? <ChevronDown /> : <ChevronUp />}
            </Button>
            {onEdit && (
              <Button
                size="icon-sm"
                variant="ghost"
                onClick={onEdit}
                aria-label="Edit call"
              >
                <Pencil />
              </Button>
            )}
            <Button
              size="icon-sm"
              variant="ghost"
              onClick={onClose}
              aria-label="Close"
            >
              <X />
            </Button>
          </Inline>
        </Inline>

        {/* Under the title, at the start of the row: a menu button would
            otherwise stretch across the panel and centre the name. */}
        {driverMenu && <Inline gap={0}>{driverMenu}</Inline>}

        {!folded && headline && (
          <Surface tone="muted" bordered={false} radius="lg" padding="sm">
            <Stack gap={1}>
              <Inline gap={2} align="center" justify="between">
                <Text as="span" variant="overline" tone="muted">
                  {headline.label}
                </Text>
                {headline.flag && (
                  <Badge tone={headline.flag.tone}>{headline.flag.label}</Badge>
                )}
              </Inline>
              <Text variant="h2" numeric>
                {headline.value}
              </Text>
              {headline.detail && (
                <Text variant="body-sm" tone="muted" numeric>
                  {headline.detail}
                </Text>
              )}
            </Stack>
          </Surface>
        )}

        {!folded && (suggestions || suggestionsLoading) && (
          <Stack gap={2}>
            <Text as="span" variant="overline" tone="muted">
              Closest drivers by drive time
            </Text>
            {suggestionsLoading ? (
              <Loading size="sm" pad="sm" />
            ) : suggestions && suggestions.length > 0 ? (
              suggestions.map((driver, index) => (
                <Surface key={driver.id} radius="lg" padding="sm">
                  <Inline gap={2} align="center" justify="between">
                    <Stack gap={0} grow>
                      <Text as="span" variant="body-sm-strong" truncate>
                        {driver.name}
                      </Text>
                      <Text as="span" variant="caption" tone="muted">
                        {driver.detail}
                      </Text>
                    </Stack>
                    {onAssignSuggestion && (
                      <Button
                        size="sm"
                        variant={index === 0 ? 'primary' : 'outline'}
                        loading={assigningDriverId === driver.id}
                        disabled={assigningDriverId !== null}
                        onClick={() => onAssignSuggestion(driver.id)}
                      >
                        Assign
                      </Button>
                    )}
                  </Inline>
                </Surface>
              ))
            ) : (
              <Text variant="body-sm" tone="muted">
                No driver has a position to measure from.
              </Text>
            )}
          </Stack>
        )}

        {!folded && (
          <Stack gap={0}>
            {facts.map((fact) => (
              <KeyValueRow
                key={fact.label}
                label={fact.label}
                density="compact"
                value={
                  fact.onSelect ? (
                    <TextLink as="button" onClick={fact.onSelect}>
                      {fact.value}
                    </TextLink>
                  ) : (
                    fact.value
                  )
                }
              />
            ))}
          </Stack>
        )}

        {!folded && timeline.length > 0 && (
          <Stack gap={1}>
            <Text as="span" variant="overline" tone="muted">
              Timeline
            </Text>
            <CallTimeline
              steps={timeline}
              onSelectStep={onSelectTimelineStep}
            />
          </Stack>
        )}

        {!folded && actions}
      </Stack>
    </Surface>
  );
});
